/**
 * Loyalty Modal - Happy Angler Club Registration
 * Turnstile-suojattu versio (explicit rendering)
 * Version: 1.1
 *
 * Changes in 1.1:
 * - PERFORMANCE: Added fetch timeout
 * - PERFORMANCE: Replaced setTimeout polling with requestIdleCallback/Promise
 * - Removed console.error from production
 */

(function() {
  'use strict';

  // Configuration
  const WORKER_URL = 'https://ha-loyalty-worker.hagrowly.workers.dev';
  const FETCH_TIMEOUT = 10000;

  // Turnstile state
  let turnstileToken = null;
  let turnstileWidgetId = null;
  let turnstileRendered = false;
  let turnstileLoadTriggered = false;

  // DOM Elements
  const modal = document.getElementById('loyalty-modal');
  const form = document.getElementById('loyalty-register-form');
  const successEl = document.getElementById('loyalty-success');
  const errorEl = document.getElementById('loyalty-error');
  const submitBtn = form?.querySelector('.loyalty-modal__submit');

  if (!modal || !form) return;

  // ========== Turnstile Callbacks (Global) ==========

  window.onLoyaltyTurnstileSuccess = function(token) {
    turnstileToken = token;
    if (submitBtn) submitBtn.disabled = false;
  };

  window.onLoyaltyTurnstileError = function() {
    turnstileToken = null;
    if (submitBtn) submitBtn.disabled = true;
    const settings = window.loyaltyModalSettings?.errors || {};
    showError(settings.turnstile || 'Vahvistus epäonnistui, yritä uudelleen');
  };

  window.onLoyaltyTurnstileExpired = function() {
    turnstileToken = null;
    if (submitBtn) submitBtn.disabled = true;
  };

  // ========== Turnstile Rendering ==========

  // Lataa Turnstile API lazy:nä — vain kun modaali avataan.
  // Skripti ei ole enää theme.liquid:ssa → ei SEO-herjoja sisältösivuilla.
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  function ensureTurnstileScript() {
    if (window.turnstile) return;
    if (document.querySelector('script[data-turnstile-loader]')) return;
    const s = document.createElement('script');
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    s.setAttribute('data-turnstile-loader', '');
    document.head.appendChild(s);
  }

  // Odota Turnstile API:n latautumista (max 5s)
  function waitForTurnstile(maxWait = 5000) {
    ensureTurnstileScript();
    return new Promise((resolve) => {
      if (window.turnstile) {
        resolve(true);
        return;
      }
      const start = Date.now();
      const check = () => {
        if (window.turnstile) {
          resolve(true);
        } else if (Date.now() - start > maxWait) {
          resolve(false);
        } else {
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    });
  }

  async function renderTurnstile() {
    // Odota Turnstile API:n latautumista
    const available = await waitForTurnstile();
    if (!available) return;

    const container = document.getElementById('loyalty-turnstile');
    if (!container) return;

    // Jos widget on jo renderöity, reset se
    if (turnstileWidgetId !== null) {
      try {
        turnstile.reset(turnstileWidgetId);
        return;
      } catch (e) {
        // Widget ei ollut enää olemassa, renderöi uusi
        turnstileWidgetId = null;
        turnstileRendered = false;
      }
    }

    // Renderöi uusi widget jos ei ole vielä renderöity
    if (!turnstileRendered) {
      const siteKey = container.dataset.sitekey;
      if (!siteKey) return;

      turnstileWidgetId = turnstile.render(container, {
        sitekey: siteKey,
        callback: window.onLoyaltyTurnstileSuccess,
        'error-callback': window.onLoyaltyTurnstileError,
        'expired-callback': window.onLoyaltyTurnstileExpired,
        theme: 'light',
        // 'invisible' is NOT a valid size for turnstile.render() — it throws and the
        // widget never issues a token, leaving the submit button permanently disabled.
        // Valid sizes: 'compact' | 'flexible' | 'normal'. 'interaction-only' keeps it
        // unobtrusive (hidden unless a challenge is required) while still issuing a token.
        size: 'flexible',
        appearance: 'interaction-only'
      });
      
      turnstileRendered = true;
    }
  }

  function resetTurnstile() {
    turnstileToken = null;
    if (turnstileWidgetId !== null && window.turnstile) {
      try {
        turnstile.reset(turnstileWidgetId);
      } catch (e) {
        // Ignore
      }
    }
    if (submitBtn) submitBtn.disabled = true;
  }

  // ========== Modal Controls ==========

  function openModal() {
    modal.removeAttribute('style');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('loyalty-modal-open');

    // Turnstile latautuu vasta kun käyttäjä alkaa täyttää kenttiä
    // (ks. form-listenerit alla).

    // Focus first input
    setTimeout(() => {
      const firstInput = form.querySelector('input');
      if (firstInput) firstInput.focus();
    }, 100);

    // Close on escape
    document.addEventListener('keydown', handleEscape);
  }

  // Turnstile lazy-trigger: lataa vasta kun käyttäjä alkaa täyttää lomaketta
  const triggerTurnstileFromForm = (e) => {
    if (turnstileLoadTriggered) return;
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return;
    turnstileLoadTriggered = true;
    renderTurnstile();
  };
  form.addEventListener('focusin', triggerTurnstileFromForm);
  form.addEventListener('input', triggerTurnstileFromForm);

  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    modal.style.opacity = '0';
    modal.style.visibility = 'hidden';
    document.body.classList.remove('loyalty-modal-open');
    document.removeEventListener('keydown', handleEscape);
    
    // Reset form after animation
    setTimeout(() => {
      resetForm();
    }, 300);
  }

  function handleEscape(e) {
    if (e.key === 'Escape') closeModal();
  }

  function resetForm() {
    form.reset();
    form.style.display = '';
    successEl.style.display = 'none';
    errorEl.style.display = 'none';

    // Reset Turnstile
    resetTurnstile();
    turnstileLoadTriggered = false;
  }

  // ========== Form Submission ==========

  async function handleSubmit(e) {
    e.preventDefault();

    const submitText = submitBtn.querySelector('.loyalty-modal__submit-text');
    const submitLoading = submitBtn.querySelector('.loyalty-modal__submit-loading');

    // Check Turnstile token
    if (!turnstileToken) {
      const settings = window.loyaltyModalSettings?.errors || {};
      showError(settings.turnstile || 'Odota vahvistuksen latautumista');
      return;
    }

    // Get form data
    const fullName = form.name.value.trim();
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const countryCode = form.countryCode.value || '';

    const data = {
      email: form.email.value.trim(),
      firstName: firstName,
      lastName: lastName,
      phone: form.phone.value.trim(),
      marketingConsent: form.marketingConsent.checked,
      countryCode: countryCode,
      turnstileToken: turnstileToken
    };

    // Validate email
    if (!data.email || !isValidEmail(data.email)) {
      const settings = window.loyaltyModalSettings?.errors || {};
      showError(settings.email_invalid || 'Tarkista sähköpostiosoite');
      return;
    }

    // Show loading state
    submitBtn.disabled = true;
    submitText.style.display = 'none';
    submitLoading.style.display = '';
    errorEl.style.display = 'none';

    // Fetch with timeout
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        signal: controller.signal
      });

      clearTimeout(timeout);
      const result = await response.json();

      if (result.success) {
        // Show success
        form.style.display = 'none';
        successEl.style.display = '';
      } else {
        // Handle errors
        const errorMessage = parseError(result.errors);
        showError(errorMessage);

        // Reset Turnstile on error
        resetTurnstile();
      }
    } catch (error) {
      clearTimeout(timeout);
      const settings = window.loyaltyModalSettings?.errors || {};
      // Check if aborted (timeout)
      if (error.name === 'AbortError') {
        showError(settings.connection || 'Pyyntö aikakatkaistiin, yritä uudelleen');
      } else {
        showError(settings.connection || 'Yhteysvirhe, yritä hetken kuluttua uudelleen');
      }

      // Reset Turnstile on error
      resetTurnstile();
    } finally {
      submitBtn.disabled = !turnstileToken;
      submitText.style.display = '';
      submitLoading.style.display = 'none';
    }
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function showError(message) {
    const errorText = errorEl.querySelector('.loyalty-modal__error-text');
    errorText.textContent = message;
    errorEl.style.display = '';
  }

  function parseError(errors) {
    const settings = window.loyaltyModalSettings?.errors || {};
    
    if (!errors || !errors.length) return settings.generic || 'Rekisteröinti epäonnistui';

    const error = errors[0];
    const errorCode = (error.message || '').toLowerCase();
    
    // Worker error codes
    if (errorCode === 'already_member') {
      return settings.already_member || 'Olet jo Happy Angler Clubin jäsen';
    }
    
    if (errorCode === 'phone_taken') {
      return settings.phone_taken || 'Tämä puhelinnumero on jo rekisteröity';
    }
    
    if (errorCode === 'turnstile_failed' || errorCode === 'missing_turnstile_token') {
      return settings.turnstile || 'Vahvistus epäonnistui, yritä uudelleen';
    }
    
    if (errorCode === 'rate_limit_exceeded') {
      return 'Liian monta yritystä, odota hetki';
    }
    
    if (errorCode === 'invalid_origin') {
      return settings.generic || 'Rekisteröinti epäonnistui';
    }
    
    // Shopify customer errors
    if (error.field?.includes('email')) {
      if (error.message?.includes('taken')) {
        return settings.email_taken || 'Tämä sähköpostiosoite on jo rekisteröity';
      }
      return settings.email_invalid || 'Tarkista sähköpostiosoite';
    }
    
    if (error.field?.includes('phone')) {
      return settings.phone_invalid || 'Tarkista puhelinnumero';
    }

    return settings.generic || error.message || 'Rekisteröinti epäonnistui';
  }

  // ========== Event Listeners ==========
  
  // Open modal from any element with data-loyalty-modal-open or href="#loyalty"
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-loyalty-modal-open], [href="#loyalty"]');
    if (trigger) {
      e.preventDefault();
      openModal();
    }
  });

  // Check if URL has #loyalty on page load
  if (window.location.hash === '#loyalty') {
    openModal();
  }

  // Close modal
  modal.addEventListener('click', (e) => {
    if (e.target.closest('[data-loyalty-modal-close]')) {
      closeModal();
    }
  });

  // Form submit
  form.addEventListener('submit', handleSubmit);

  // Expose to global scope for programmatic control
  window.loyaltyModal = {
    open: openModal,
    close: closeModal
  };

})();